import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Route guard for admin-only pages.
//
// Options:
//   - requireSuperadmin: strict superadmin-only (system settings, etc.)
//   - requireRoles: array of allowed userRoles. Use for routes shared
//     between specific roles (e.g. CRM is open to superadmin + salesperson).
//
// Non-authorized users (signed in but lacking the required role) get
// bounced to / instead of /login.
const ProtectedRoute = ({ children, requireSuperadmin = false, requireRoles = null }) => {
  const { currentUser, isAdmin, userRole, managerAssignments } = useAuth();

  // Base access: must be signed in and have at least one privileged role, OR
  // hold an org_managers row. recomputeUserRole only promotes users.role to
  // org_owner/salesperson, so a plain manager stays role='user' — gating on
  // role alone locked them out of the app they were just invited to. This
  // matches the manager fallback AuthContext.signIn already applies.
  const isOrgManager = (managerAssignments || []).length > 0;
  const hasAccess = currentUser && (isAdmin || userRole === 'org_owner' || userRole === 'salesperson' || userRole === 'affiliate' || isOrgManager);
  if (!hasAccess) {
    return <Navigate to="/login" replace />;
  }

  // Stricter: superadmin-only routes.
  if (requireSuperadmin && userRole !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  // Role allow-list (use for routes shared by a specific set of roles).
  if (Array.isArray(requireRoles) && requireRoles.length > 0 && !requireRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;